import { useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  Container,
  CssBaseline,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { AgeInfo } from "../form/components/AgeInfo";
import { CheckBoxLabel } from "../form/components/CheckboxLabel";
import { AuthHeader } from "../form/components/Headers";
import ModalLoadingFull from "../utils/ModalLoadingFull";
import { postData } from "../utils/APIUtilities";
import { redirectUrlAuth, urlParams } from "../utils/urlParams";
import { postNotification } from "../utils/Notifications";
import { getEnvironmentVariable } from "../global/environmentVariables";

export default function OICSignup({ tokens, identityToken }) {
  const [processing, setProcessing] = useState(false);

  const { state, redirect_uri } = urlParams();

  const _redirect_uri = redirect_uri || localStorage?.getItem("redirect_uri");

  const userId = identityToken?.userId;

  const onSubmit = async (values) => {
    try {
      setProcessing(true);
      const { firstName, lastName, policy, marketing } = values;
      if (!firstName) throw new Error("missing firstName");
      if (!lastName) throw new Error("missing lastName");
      if (!policy) throw new Error("missing policy");

      const headers = {
        Authorization: `Bearer ${tokens.accessToken}`,
        Env: "bcp",
      };

      await postData(
        `/users/${userId}`,
        {
          body: {
            set: {
              firstName,
              lastName,
              sendNotifications: marketing?.toLowerCase() === "yes",
            },
            unset: {
              postExternalLinkFlow: true,
            },
          },
        },
        headers
      );

      // New endpoint, should return the auth code
      const apiData = await postData(
        "/oauth/storetoken",
        {
          token: tokens.refreshToken,
          redirect_uri: _redirect_uri,
        },
        null,
        getEnvironmentVariable("API_URL").replace("/v1", "")
      );

      // Regular login redirect
      await redirectUrlAuth({
        cognitoProps: {
          code: apiData?.code || apiData?.authorizationCode,
          state: state || localStorage?.getItem("state"),
        },
        redirect_uri: apiData?.redirect_uri || _redirect_uri,
      });
    } catch (err) {
      postNotification("error", err);
      setProcessing(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      {processing && <ModalLoadingFull />}
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <AuthHeader text="Confirm Account Details" />

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Formik
              initialValues={{
                email: identityToken?.email || null,
                firstName: identityToken?.given_name || null,
                lastName: identityToken?.family_name || null,
                policy: false,
                marketing: "",
              }}
              enableReinitialize
              validationSchema={Yup.object().shape({
                email: Yup.string()
                  .max(128)
                  .email("Must be a valid email")
                  .required("Required"),
                firstName: Yup.string()
                  .nullable()
                  .max(128)
                  .required("Required"),
                lastName: Yup.string().nullable().max(128).required("Required"),
                policy: Yup.boolean().oneOf([true], "Required"),
                marketing: Yup.string().nullable().required("Required"),
              })}
              onSubmit={onSubmit.bind(this)}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                setFieldValue,
                touched,
                values,
              }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && errors.email}
                        autoComplete="email"
                        name="email"
                        value={values.email}
                        label="Email"
                        variant="outlined"
                        required
                        fullWidth
                        onBlur={handleBlur}
                        onChange={(e) => {
                          const trimmedString = e.target.value
                            .trim()
                            .toLowerCase();
                          setFieldValue("email", trimmedString);
                        }}
                        disabled
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        error={Boolean(touched.firstName && errors.firstName)}
                        helperText={touched.firstName && errors.firstName}
                        autoComplete="firstName"
                        name="firstName"
                        value={values.firstName}
                        label="First Name"
                        variant="outlined"
                        required
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        error={Boolean(touched.lastName && errors.lastName)}
                        helperText={touched.lastName && errors.lastName}
                        autoComplete="lastName"
                        name="lastName"
                        value={values.lastName}
                        label="Last Name"
                        variant="outlined"
                        required
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl required>
                        <FormLabel>
                          Sometimes we send out exciting emails about upcoming
                          events, special offers, and deals from our partners.
                          Would you like to receive these emails?
                        </FormLabel>

                        <RadioGroup row>
                          <FormControlLabel
                            value={values.marketing}
                            control={
                              <Radio
                                name="marketing"
                                checked={values.marketing === "Yes"}
                                defaultChecked={false}
                                onClick={(e) => {
                                  setFieldValue("marketing", "Yes");
                                }}
                              />
                            }
                            label="Yes"
                          />
                          <FormControlLabel
                            value={values.marketing}
                            control={
                              <Radio
                                name="marketing"
                                checked={values.marketing === "No"}
                                defaultChecked={false}
                                onClick={(e) => {
                                  setFieldValue("marketing", "No");
                                }}
                              />
                            }
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                      {Boolean(touched.marketing && errors.marketing) && (
                        <FormHelperText error>
                          {errors.marketing}
                        </FormHelperText>
                      )}
                    </Grid>

                    <AgeInfo />

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="policy"
                            checked={values.policy}
                            onChange={setFieldValue.bind(
                              this,
                              "policy",
                              !values.policy
                            )}
                          />
                        }
                        label={
                          <CheckBoxLabel
                            text="I have read and agree with the Terms and Conditions"
                            required
                          />
                        }
                      />

                      {Boolean(touched.policy && errors.policy) && (
                        <FormHelperText error>{errors.policy}</FormHelperText>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Button type="submit" fullWidth variant="contained">
                        Sign Up
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
